export const journeyStages = [
  'Simulação',
  'Dados',
  'Documentos',
  'Em análise',
  'Contrato',
] as const

export const allowanceSteps = [
  'fac',
  'valor',
  'plano',
  'eu',
  'end',
  'civil',
  'pais',
  'uso',
  'renda',
  'msg',
  'traj',
  'planos',
  'conta',
  'docs',
  'analise',
  'proposta',
  'assinar',
  'fim',
] as const

export type AllowanceStep = (typeof allowanceSteps)[number]

const simulationSteps = new Set<AllowanceStep>(['fac', 'valor', 'plano'])
const dataSteps = new Set<AllowanceStep>([
  'eu',
  'end',
  'civil',
  'pais',
  'uso',
  'renda',
  'msg',
  'traj',
  'planos',
  'conta',
])

export function stageOf(step: AllowanceStep) {
  if (simulationSteps.has(step)) return 0
  if (dataSteps.has(step)) return 1
  if (step === 'docs') return 2
  if (step === 'analise') return 3
  return 4
}

export type AllowanceFacts = {
  collegePrivate: boolean
  hasSimulation: boolean
  status: string
  serverStep: string
  completed: Partial<Record<AllowanceStep, boolean>>
}

/** Em faculdade privada, a tela de renda também pergunta bolsa e financiamento. */
export function visibleSteps() {
  return [...allowanceSteps]
}

export type StepPerson = {
  birthdate?: string | null
  gender?: string | null
  marital_status?: string | null
  politically_exposed?: boolean | null
  us_person?: boolean | null
  spouse_name?: string | null
  spouse_tax_id?: string | null
  father_not_registered?: boolean | null
  father_name?: string | null
  father_tax_id?: string | null
  father_education_level?: string | null
  mother_name?: string | null
  mother_tax_id?: string | null
  mother_education_level?: string | null
  credit_purposes?: string[]
  monthly_family_income?: unknown
  high_school_type?: string | null
  residency_timing?: string | null
  address?: unknown[]
  bank_account?: unknown[]
}

export function isMarried(status: string | null | undefined) {
  return status === 'married' || status === 'civil_union'
}

export function isStepComplete(
  step: AllowanceStep,
  person: StepPerson | undefined,
  extras: {
    college?: boolean
    graduation?: boolean
    hasSimulation?: boolean
    documents?: string[]
  } = {},
) {
  if ((step === 'fac' || step === 'valor' || step === 'plano') && extras.hasSimulation) {
    return true
  }
  switch (step) {
    case 'fac':
      return Boolean(extras.college && extras.graduation)
    case 'valor':
    case 'plano':
      return false
    case 'eu':
      return Boolean(person?.birthdate && person.gender)
    case 'end':
      return Boolean(person?.address?.length)
    case 'civil': {
      if (!person?.marital_status) return false
      if (person.politically_exposed == null || person.us_person == null) return false
      if (isMarried(person.marital_status) && (!person.spouse_name || !person.spouse_tax_id)) {
        return false
      }
      return true
    }
    case 'pais': {
      if (!person?.mother_name || !person.mother_tax_id || !person.mother_education_level) {
        return false
      }
      if (person.father_not_registered) return true
      return Boolean(person.father_name && person.father_tax_id && person.father_education_level)
    }
    case 'uso':
      return Boolean(person?.credit_purposes?.length)
    case 'msg':
      return true
    case 'renda':
      return person?.monthly_family_income != null && person.monthly_family_income !== ''
    case 'traj':
      return Boolean(person?.high_school_type)
    case 'planos':
      return Boolean(person?.residency_timing)
    case 'conta':
      return Boolean(person?.bank_account?.length)
    case 'docs':
      return requiredDocuments.every((type) => extras.documents?.includes(type))
    default:
      return true
  }
}

export function resumeStep(facts: AllowanceFacts): AllowanceStep {
  if (facts.status === 'done') return 'fim'
  if (facts.status === 'denied') return 'analise'
  if (
    facts.serverStep === 'agreement_signature' ||
    facts.serverStep === 'credit_note_signature'
  ) {
    return 'assinar'
  }
  if (facts.serverStep === 'proposal' || facts.status === 'approved') return 'proposta'
  if (
    facts.status === 'analysing' ||
    facts.serverStep === 'committee_analysis' ||
    facts.serverStep === 'documents_analysis'
  ) {
    return 'analise'
  }
  if (facts.serverStep === 'proponets_documents' || facts.status === 'review') {
    return 'docs'
  }
  if (!facts.hasSimulation) return facts.completed.fac ? 'valor' : 'fac'

  const nextData = visibleSteps().find(
    (step) => dataSteps.has(step) && !facts.completed[step],
  )
  return nextData ?? 'docs'
}

export function resolveStep(
  requested: string | undefined,
  facts: AllowanceFacts,
): AllowanceStep {
  const visible = visibleSteps()
  const resume = resumeStep(facts)
  const requestedStep = visible.find((step) => step === requested)
  if (!requestedStep) return resume
  if (!facts.hasSimulation && simulationSteps.has(requestedStep)) {
    if (!facts.completed.fac && requestedStep !== 'fac') return 'fac'
    return requestedStep
  }
  if (visible.indexOf(requestedStep) > visible.indexOf(resume)) return resume
  return requestedStep
}

export function stepProgress(step: AllowanceStep) {
  const inStage = visibleSteps().filter((item) => stageOf(item) === stageOf(step))
  const index = Math.max(inStage.indexOf(step), 0)
  return { current: index + 1, total: inStage.length }
}

/** A matrícula e o histórico escolar vão no mesmo campo. */
export const requiredDocuments = ['personal', 'proof_of_registration'] as const

export function documentList(collegePrivate: boolean) {
  return collegePrivate
    ? [...requiredDocuments, 'address' as const, 'proof_of_scholarship' as const]
    : [...requiredDocuments, 'address' as const]
}
